import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, FileText, Plus, Trash2, MessageSquare, X } from 'lucide-react';
import { Button, ThemedText } from '../../components';
import { knowledgeBaseService } from '../../api';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${(p) => p.theme.spacing.md};
  height: calc(100vh - 320px);
  min-height: 500px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Sidebar = styled.div`
  background: ${(p) => p.theme.colors.backgroundSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  padding: ${(p) => p.theme.spacing.sm};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SessionItem = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.tint : 'transparent')};
  background: ${(p) => (p.$active ? p.theme.colors.background : 'transparent')};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  color: ${(p) => p.theme.colors.text};
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: ${(p) => p.theme.colors.background};
  }
`;

const SessionTitle = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionMeta = styled.div`
  font-size: 10px;
  color: ${(p) => p.theme.colors.muted};
  margin-top: 2px;
`;

const ChatPane = styled.div`
  background: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${(p) => p.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Bubble = styled.div`
  max-width: 80%;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: ${(p) => (p.$assistant ? 'normal' : 'pre-wrap')};
  word-break: break-word;
  background: ${(p) => (p.$assistant ? p.theme.colors.backgroundSecondary : p.theme.colors.tint)};
  color: ${(p) => (p.$assistant ? p.theme.colors.text : '#fff')};
  align-self: ${(p) => (p.$assistant ? 'flex-start' : 'flex-end')};
  border: 1px solid ${(p) => p.theme.colors.border};
`;

const MarkdownBody = styled.div`
  & > *:first-child { margin-top: 0; }
  & > *:last-child { margin-bottom: 0; }
  p { margin: 0 0 8px 0; }
  ul, ol { margin: 0 0 8px 0; padding-left: 22px; }
  li { margin: 2px 0; }
  li > p { margin: 0; }
  h1, h2, h3, h4 { margin: 12px 0 6px 0; font-weight: 600; }
  h1 { font-size: 16px; }
  h2 { font-size: 15px; }
  h3 { font-size: 14px; }
  h4 { font-size: 13px; }
  strong { font-weight: 600; }
  em { font-style: italic; }
  code {
    background: ${(p) => p.theme.colors.background};
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 12px;
    font-family: 'SF Mono', Monaco, Consolas, monospace;
  }
  pre {
    background: ${(p) => p.theme.colors.background};
    padding: 8px 10px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0 0 8px 0;
  }
  pre code { background: transparent; padding: 0; }
  blockquote {
    border-left: 3px solid ${(p) => p.theme.colors.border};
    padding-left: 10px;
    color: ${(p) => p.theme.colors.muted};
    margin: 0 0 8px 0;
  }
  a {
    color: ${(p) => p.theme.colors.tint};
    text-decoration: underline;
  }
  table {
    border-collapse: collapse;
    margin: 0 0 8px 0;
  }
  th, td {
    border: 1px solid ${(p) => p.theme.colors.border};
    padding: 4px 8px;
    font-size: 12px;
  }
  th { background: ${(p) => p.theme.colors.background}; font-weight: 600; }
  hr { border: none; border-top: 1px solid ${(p) => p.theme.colors.border}; margin: 10px 0; }
`;

const Citations = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed ${(p) => p.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CitationCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  cursor: pointer;
  color: ${(p) => p.theme.colors.text};

  &:hover {
    border-color: ${(p) => p.theme.colors.tint};
  }
`;

const InputRow = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.border};
  padding: ${(p) => p.theme.spacing.md};
  display: flex;
  gap: 8px;
`;

const Textarea = styled.textarea`
  flex: 1;
  resize: none;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
  font-family: inherit;
  font-size: 13px;
  min-height: 44px;
  max-height: 160px;
`;

const PdfModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PdfModalBody = styled.div`
  background: ${(p) => p.theme.colors.background};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  width: min(900px, 100%);
  height: min(80vh, 800px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PdfModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

function getCitationName(c) {
  if (c?.documentName) return c.documentName;
  if (!c?.documentS3Key) return 'Document';
  return c.documentS3Key.split('/').pop() || 'Document';
}

export default function KnowledgeChatView({ organizationId, theme }) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const messagesRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const list = await knowledgeBaseService.listSessions(organizationId);
      setSessions(list || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load sessions');
    }
  }, [organizationId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const openSession = async (sessionId) => {
    try {
      const detail = await knowledgeBaseService.getSession(organizationId, sessionId);
      setActiveSessionId(sessionId);
      setMessages(detail.messages || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load session');
    }
  };

  const newSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
  };

  const handleSend = async () => {
    const query = input.trim();
    if (!query || sending) return;

    setSending(true);
    const localUserMsg = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: query,
      citations: [],
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localUserMsg]);
    setInput('');

    try {
      const response = await knowledgeBaseService.chat(organizationId, query, activeSessionId);
      const assistantMsg = {
        id: response.messageId,
        role: 'assistant',
        content: response.answer,
        citations: response.citations || [],
        followUpQuestions: response.followUpQuestions || [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
      if (!activeSessionId) {
        setActiveSessionId(response.sessionId);
        loadSessions();
      }
    } catch (err) {
      toast.error(err?.message || 'Chat request failed');
      setMessages((prev) => prev.filter((m) => m.id !== localUserMsg.id));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await knowledgeBaseService.deleteSession(organizationId, sessionId);
      if (activeSessionId === sessionId) newSession();
      await loadSessions();
    } catch (err) {
      toast.error(err?.message || 'Delete failed');
    }
  };

  const openCitation = async (citation) => {
    // Server-side enrichment now puts documentId on the citation; fall back to
    // a list-and-match only if the server didn't resolve it (e.g. an old session
    // whose stored CitationsJson predates this fix).
    try {
      let documentId = citation?.documentId ?? null;
      if (!documentId) {
        if (!citation?.documentS3Key) return;
        const docs = await knowledgeBaseService.listDocuments(organizationId);
        const match = docs.find((d) => d.fileName === getCitationName(citation));
        if (!match) {
          toast.error('Source document not found');
          return;
        }
        documentId = match.id;
      }
      let url;
      if (citation.pageNumber && citation.pageNumber > 0) {
        const pageRes = await knowledgeBaseService.getDocumentPageUrl(organizationId, documentId, citation.pageNumber);
        url = pageRes.pageUrl || pageRes.fullDocUrl;
      } else {
        const dl = await knowledgeBaseService.getDocumentDownloadUrl(organizationId, documentId);
        url = dl.url;
      }
      setPdfPreview({ url, name: getCitationName(citation), pageNumber: citation.pageNumber });
    } catch (err) {
      toast.error(err?.message || 'Could not open citation');
    }
  };

  return (
    <>
      <Layout theme={theme}>
        <Sidebar theme={theme}>
          <Button variant="primary" size="sm" onClick={newSession} style={{ marginBottom: 4 }}>
            <Plus size={14} /> New chat
          </Button>
          {sessions.length === 0 && (
            <div style={{ fontSize: 12, color: theme.colors.muted, padding: 8, textAlign: 'center' }}>
              No conversations yet
            </div>
          )}
          {sessions.map((s) => (
            <SessionItem
              theme={theme}
              key={s.id}
              $active={s.id === activeSessionId}
              onClick={() => openSession(s.id)}
            >
              <MessageSquare size={12} style={{ marginTop: 2, color: theme.colors.muted, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <SessionTitle>{s.title || 'Untitled'}</SessionTitle>
                <SessionMeta theme={theme}>
                  {s.messageCount} message{s.messageCount === 1 ? '' : 's'}
                </SessionMeta>
              </div>
              <button
                onClick={(e) => handleDeleteSession(s.id, e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.muted }}
                title="Delete"
              >
                <Trash2 size={11} />
              </button>
            </SessionItem>
          ))}
        </Sidebar>

        <ChatPane theme={theme}>
          <Messages theme={theme} ref={messagesRef}>
            {messages.length === 0 && !sending && (
              <div style={{ margin: 'auto', textAlign: 'center', color: theme.colors.muted, fontSize: 13 }}>
                <ThemedText type="subtitle" style={{ marginBottom: 8 }}>
                  Ask anything from your company documents
                </ThemedText>
                <div>Try "What's our refund policy?" or "Show me the latest training manual."</div>
              </div>
            )}
            {messages.map((m) => (
              <Bubble theme={theme} key={m.id} $assistant={m.role === 'assistant'}>
                {m.role === 'assistant' ? (
                  <MarkdownBody theme={theme}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ''}</ReactMarkdown>
                  </MarkdownBody>
                ) : (
                  m.content
                )}
                {m.role === 'assistant' && m.citations?.length > 0 && (
                  <Citations theme={theme}>
                    {m.citations.map((c, i) => (
                      <CitationCard theme={theme} key={i} onClick={() => openCitation(c)}>
                        <FileText size={14} style={{ flexShrink: 0, marginTop: 1, color: theme.colors.muted }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 12 }}>
                            {getCitationName(c)}
                            {c.pageNumber && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: theme.colors.muted }}>
                                p.{c.pageNumber}
                              </span>
                            )}
                          </div>
                          {c.snippet && (
                            <div style={{ fontSize: 11, color: theme.colors.muted, marginTop: 2 }}>
                              {c.snippet.length > 200 ? c.snippet.slice(0, 200) + '…' : c.snippet}
                            </div>
                          )}
                        </div>
                      </CitationCard>
                    ))}
                  </Citations>
                )}
              </Bubble>
            ))}
            {sending && (
              <Bubble theme={theme} $assistant>
                <span style={{ opacity: 0.7 }}>Thinking…</span>
              </Bubble>
            )}
          </Messages>

          <InputRow theme={theme}>
            <Textarea
              theme={theme}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask a question…"
              disabled={sending}
            />
            <Button variant="primary" size="sm" onClick={handleSend} disabled={sending || !input.trim()}>
              <Send size={14} />
            </Button>
          </InputRow>
        </ChatPane>
      </Layout>

      <PdfModalOverlay $open={!!pdfPreview}>
        {pdfPreview && (
          <PdfModalBody theme={theme}>
            <PdfModalHeader theme={theme}>
              <ThemedText type="subtitle" style={{ fontSize: 14 }}>
                {pdfPreview.name}
                {pdfPreview.pageNumber && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: theme.colors.muted }}>
                    page {pdfPreview.pageNumber}
                  </span>
                )}
              </ThemedText>
              <Button variant="secondary" size="sm" onClick={() => setPdfPreview(null)}>
                <X size={14} />
              </Button>
            </PdfModalHeader>
            <iframe
              title="document-preview"
              src={pdfPreview.url}
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </PdfModalBody>
        )}
      </PdfModalOverlay>
    </>
  );
}
